/**
 * P21 – Giao việc, điều phối (docs/phases/P21.md §9.16, DEC-249).
 * Lệnh tăng ca, lệnh làm Chủ nhật, lệnh điều động: TP / GĐ / HR giao → NV nhận → tới nơi (ảnh + GPS) → báo cáo → xong → người giao xác nhận.
 * Lệnh đã xác nhận mới vào bảng công: tăng ca cộng giờ TC, Chủ nhật đếm ngày CN, điều động đủ 1 công và bỏ trễ / về sớm.
 */
import { timeToMin } from './attendance';

export const WORK_ORDER_KINDS = ['overtime', 'sunday', 'dispatch', 'task'] as const;
export type WorkOrderKind = (typeof WORK_ORDER_KINDS)[number];
/** Lệnh vào bảng công (tăng ca / Chủ nhật / điều động); 'task' = Việc thường có hạn chót, không vào bảng công (DEC-322) */
export const ATTENDANCE_ORDER_KINDS = ['overtime', 'sunday', 'dispatch'] as const satisfies readonly WorkOrderKind[];
export const WORK_ORDER_KIND_LABELS: Record<WorkOrderKind, string> = {
  overtime: 'Tăng ca',
  sunday: 'Làm Chủ nhật',
  dispatch: 'Điều động',
  task: 'Việc thường',
};

/** pending = NV tự khai tăng ca chờ TP / GĐ duyệt; open = lệnh đang chạy */
export const WORK_ORDER_STATUSES = ['pending', 'open', 'rejected', 'cancelled'] as const;
export type WorkOrderStatus = (typeof WORK_ORDER_STATUSES)[number];
export const WORK_ORDER_STATUS_LABELS: Record<WorkOrderStatus, string> = {
  pending: 'Chờ duyệt',
  open: 'Đang giao',
  rejected: 'Từ chối',
  cancelled: 'Đã hủy',
};

export const WORK_ASSIGNEE_STATUSES = ['assigned', 'accepted', 'arrived', 'done', 'confirmed', 'declined', 'returned'] as const;
export type WorkAssigneeStatus = (typeof WORK_ASSIGNEE_STATUSES)[number];
export const WORK_ASSIGNEE_STATUS_LABELS: Record<WorkAssigneeStatus, string> = {
  assigned: 'Chưa nhận',
  accepted: 'Đã nhận',
  arrived: 'Đã tới',
  done: 'Xong, chờ xác nhận',
  confirmed: 'Đã xác nhận',
  declined: 'Không xác nhận',
  returned: 'Trả lại làm lại',
};

/** Lệnh giao trước giờ bắt đầu ít nhất (phút); GĐ không bị chặn (kế hoạch P21 §4.6). */
export const WORK_ORDER_MIN_LEAD_MINUTES = 120;
/** Giờ tăng ca tối đa một lệnh / một ngày */
export const WORK_ORDER_MAX_OT_HOURS = 12;

/** Số giờ giữa 'HH:MM' bắt đầu và kết thúc (cùng ngày), 2 chữ số lẻ; sai giờ → 0. */
export function workOrderHours(start: string | null | undefined, end: string | null | undefined): number {
  if (!start || !end) return 0;
  const m = timeToMin(end) - timeToMin(start);
  return m > 0 ? Math.round((m / 60) * 100) / 100 : 0;
}

/** Hiện giờ tăng ca trong ô công: 4 → "+4h", 1.5 → "+1,5h" */
export function fmtOtBadge(hours: number): string {
  if (!hours) return '';
  const v = Math.round(hours * 100) / 100;
  return `+${String(v).replace('.', ',')}h`;
}

// ---------------------------------------------------------------------------
// Việc thường có hạn chót (DEC-322)
// ---------------------------------------------------------------------------
export const WORK_TASK_PRIORITIES = ['normal', 'high'] as const;
export type WorkTaskPriority = (typeof WORK_TASK_PRIORITIES)[number];
export const WORK_TASK_PRIORITY_LABELS: Record<WorkTaskPriority, string> = { normal: 'Thường', high: 'Cao' };
/** Nhắc người nhận trước hạn (phút) mặc định khi giao việc */
export const WORK_TASK_DEFAULT_REMIND_MINUTES = 120;

/** Tình trạng hạn của 1 người nhận việc thường – dùng cho cột "Hạn", Việc của tôi và Bảng điểm tuần. */
export type WorkTaskDeadline = 'overdue' | 'today' | 'upcoming' | 'waiting' | 'on_time' | 'late' | 'returned' | 'closed';

/**
 * - Chưa xong: quá hạn / hạn hôm nay (giờ VN) / còn hạn; bị trả lại = 'returned' (vẫn tính quá hạn nếu đã qua hạn).
 * - Đã báo xong chờ xác nhận = 'waiting'. Đã xác nhận: xong đúng hạn nếu lần báo xong cuối ≤ hạn chót, ngược lại xong trễ.
 * - Không xác nhận (từ chối) = 'closed'.
 */
export function workTaskDeadline(
  a: { status: WorkAssigneeStatus; doneAt: string | Date | null },
  dueAt: string | Date,
  now: Date = new Date(),
): WorkTaskDeadline {
  const due = new Date(dueAt).getTime();
  if (a.status === 'declined') return 'closed';
  if (a.status === 'confirmed') return a.doneAt && new Date(a.doneAt).getTime() <= due ? 'on_time' : 'late';
  if (a.status === 'done') return 'waiting';
  if (now.getTime() > due) return 'overdue';
  if (a.status === 'returned') return 'returned';
  const vnDay = (t: number) => new Date(t + 7 * 3600_000).toISOString().slice(0, 10);
  return vnDay(due) === vnDay(now.getTime()) ? 'today' : 'upcoming';
}

/** "Quá hạn 1 ngày 3 giờ", "Còn 2 ngày", "Còn 45 phút" – chênh lệch giữa hạn chót và bây giờ. */
export function fmtTaskLeft(dueAt: string | Date, now: Date = new Date()): string {
  const diff = new Date(dueAt).getTime() - now.getTime();
  const m = Math.round(Math.abs(diff) / 60_000);
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  const span = d ? `${d} ngày${h && d < 3 ? ` ${h} giờ` : ''}` : h ? `${h} giờ${m % 60 && h < 3 ? ` ${m % 60} phút` : ''}` : `${m} phút`;
  return diff < 0 ? `Quá hạn ${span}` : `Còn ${span}`;
}
